import { Map as MapIcon, SquareCheck, Trophy, X } from 'lucide-react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { MiniMap, type ViewportFrac } from './mini-map';

import type { Task } from '@/types/experience';

import { useTabTrap } from '@/lib/use-tab-trap';

/**
 * Overview map for the builder: wraps the shared `MiniMap` (navigate mode) in a docked panel (desktop)
 * or modal (mobile), so the whole branching shape is visible and clicking a block jumps the board there
 * (without closing the map — repeated navigation). `currentTaskId` rings the task currently in view.
 * `boardScrollRef` (the board's scroll container) drives the draggable viewport box and lets a hovered
 * block ring its workspace card. Read-only — it navigates, never edits.
 */

/** Track the board scroll container's visible region as fractions of its scrollable content.
 * `revision` (the tasks array) forces a recompute when structure changes — opening/closing a pathway
 * shifts scrollHeight with neither a scroll event nor a container resize. */
function useViewportFrac(boardScrollRef: React.RefObject<HTMLElement | null> | undefined, revision: unknown): ViewportFrac | null {
  const [vp, setVp] = useState<ViewportFrac | null>(null);
  const update = useCallback(() => {
    const el = boardScrollRef?.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = el;
    if (scrollHeight <= 0 || scrollWidth <= 0) { setVp(null); return; }
    setVp({
      topFrac: scrollTop / scrollHeight,
      heightFrac: Math.min(1, clientHeight / scrollHeight),
      leftFrac: scrollLeft / scrollWidth,
      widthFrac: Math.min(1, clientWidth / scrollWidth),
    });
  }, [boardScrollRef]);
  useEffect(() => {
    const el = boardScrollRef?.current;
    if (!el) return;
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => { el.removeEventListener('scroll', update); ro.disconnect(); };
  }, [update, boardScrollRef]);
  // Structure changed (pathway open/close, add/remove task) → recompute after the board re-lays out.
  useLayoutEffect(() => { update(); }, [revision, update]);
  return vp;
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

export default function BuilderMapModal({
  tasks,
  currentTaskId,
  onJumpToTask,
  onHoverTask,
  boardScrollRef,
  onClose,
}: {
  tasks: Task[];
  currentTaskId?: string | null;
  onJumpToTask: (taskId: string) => void;
  onHoverTask?: (taskId: string | null) => void;
  boardScrollRef?: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const totalPoints = tasks.reduce((sum, tk) => sum + (tk.points ?? 0), 0);
  const viewport = useViewportFrac(boardScrollRef, tasks);
  const scrollToFrac = (topFrac: number, leftFrac: number) => {
    const el = boardScrollRef?.current;
    if (!el) return;
    el.scrollTo({ top: topFrac * el.scrollHeight, left: leftFrac * el.scrollWidth, behavior: 'auto' });
  };

  const closeRef = useRef<HTMLButtonElement>(null);
  // On mobile the overview is a true modal → announce it as a dialog + trap Tab. On desktop it's a
  // docked side panel (complementary), so neither applies.
  const titleId = useId();
  const dialogRef = useTabTrap<HTMLDivElement>(isMobile);
  useEffect(() => { closeRef.current?.focus(); }, []);
  // Escape closes from anywhere — the keyboard path; the backdrop tap is a mouse convenience on top.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function jump(id: string) {
    onJumpToTask(id);
    if (isMobile) onClose();
  }

  const panel = (
    <div
      ref={isMobile ? dialogRef : undefined}
      role={isMobile ? 'dialog' : undefined}
      aria-modal={isMobile ? true : undefined}
      aria-labelledby={isMobile ? titleId : undefined}
      className={`flex flex-col gap-4 rounded-xl border border-ws-border bg-ws-elevated p-5 shadow-xl ${
        isMobile ? 'relative max-h-[80vh] w-full max-w-sm' : 'h-full w-96'
      }`}
    >
      <div className='flex shrink-0 flex-col gap-2'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <MapIcon className='h-5 w-5 text-ws-text' strokeWidth={2.5} />
            <h2 id={titleId} className='text-lg font-semibold text-ws-text'>{t('experience.map.title')}</h2>
          </div>
          <button
            ref={closeRef}
            onClick={onClose}
            className='flex h-7 w-7 items-center justify-center rounded-md text-ws-muted hover:bg-ws-hover hover:text-ws-text'
            aria-label={t('common.close')}
          >
            <X className='h-4 w-4' />
          </button>
        </div>
        <div className='flex items-center gap-3 text-xs text-ws-muted'>
          <span className='flex items-center gap-1'><SquareCheck className='h-3 w-3' />{t('experience.map.tasksCount', { count: tasks.length })}</span>
          <span className='flex items-center gap-1'><Trophy className='h-3 w-3' />{t('experience.map.pointsCount', { count: totalPoints })}</span>
        </div>
      </div>

      <div className='relative flex-1 overflow-auto rounded-lg border border-ws-border bg-ws-surface p-3'>
        <MiniMap
          tasks={tasks}
          mode='navigate'
          currentTaskId={currentTaskId}
          onJump={jump}
          onHoverTask={onHoverTask}
          viewport={isMobile ? null : viewport}
          onScrollToFrac={scrollToFrac}
        />
      </div>
    </div>
  );

  if (isMobile) {
    return createPortal(
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- decorative dismiss overlay; keyboard path is Escape + the focused close button */}
        <div className='absolute inset-0 bg-black/50' onClick={onClose} />
        {panel}
      </div>,
      document.body,
    );
  }

  // z-40 so the panel sits above ALL board chrome (End caps, pathway bars, ports); pr-4 keeps the panel
  // clear of the board's right-edge scrollbar so it stays usable.
  return <div className='pointer-events-none absolute inset-y-0 right-0 z-40 py-2 pl-2 pr-4'><div className='pointer-events-auto h-full'>{panel}</div></div>;
}
